/**
 * R-16 — Amendment provenance markers on citation tokens.
 *
 * Surfaces SED amendments (`SEDAmendment` in store / Firestore) and report
 * text corrections (`EditRecord` on `SiteRagReport.editHistory`) as Unicode
 * superscript markers on affected `[Ref N]` citations plus investor-facing
 * footnotes. Does not mutate amendment storage — projection only.
 */
import type {
  DataSource,
  EditRecord,
  SiteRagReport,
} from '../../types/siteRag';
import type { SEDAmendment } from '../../types/sedAmendments';

export interface SectionAmendment {
  amendmentId: string;
  fieldRange: { fieldPath: string };
  originalText: string;
  amendedText: string;
  amendedBy: string;
  amendedAt: string;
  rationale?: string;
  citationsAffected: number[];
}

export interface AmendmentFootnote {
  markerId: string;
  markerChar: string;
  amendmentId: string;
  body: string;
  amendedBy: string;
  amendedAt: string;
  citationsAffected: number[];
}

export interface AmendmentChainLink {
  amendmentId: string;
  siteId: string;
  reportId: string;
  variablePath: string;
  fieldPath: string;
  status: SEDAmendment['status'];
  amendedAt: string;
  amendedBy: string;
  previousAmendmentId?: string;
}

type DataSourceWithAmendment = DataSource & {
  amendedSinceVersion?: number;
  amendmentMarkerIds?: string[];
};

export type AmendmentStampedReport = SiteRagReport & {
  sectionAmendments?: Record<string, SectionAmendment[]>;
  amendmentFootnotes?: AmendmentFootnote[];
  citationAmendmentMarkers?: Record<number, string[]>;
  references?: DataSourceWithAmendment[];
};

/** Unicode superscript letters a–z (investor-facing, subtle). */
export const AMENDMENT_SUPERSCRIPT_AZ =
  'ᵃᵇᶜᵈᵉᶠᵍʰⁱʲᵏˡᵐⁿᵒᵖᵠʳˢᵗᵘᵛʷˣʸᶻ';

const MARKER_IDS = 'abcdefghijklmnopqrstuvwxyz'.split('');

const REF_TOKEN_RE = /\[Ref\s+(\d+)\]/gi;

/** Map marker id (`a`) → display char (`ᵃ`). */
export function markerIdToSuperscript(markerId: string): string {
  const idx = MARKER_IDS.indexOf(markerId);
  return idx >= 0 ? AMENDMENT_SUPERSCRIPT_AZ[idx]! : markerId;
}

/** Active amendments that should surface on citations. */
export function isActiveAmendmentStatus(status: SEDAmendment['status']): boolean {
  return status === 'applied' || status === 'approved' || status === 'pending';
}

function domainFromVariablePath(variablePath: string): string {
  return variablePath.split('.')[0] ?? variablePath;
}

function extractRefNumbers(text: string): number[] {
  const out = new Set<number>();
  REF_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = REF_TOKEN_RE.exec(text)) !== null) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n >= 1) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

/** Ref indices whose `DataSource.field` overlaps an amendment domain / path. */
export function refsAffectedByDomain(
  domainOrPath: string,
  refs: DataSource[],
): number[] {
  const domain = domainOrPath.includes('.') ? domainFromVariablePath(domainOrPath) : domainOrPath;
  const out: number[] = [];
  refs.forEach((ref, i) => {
    if (!ref.field) return;
    if (ref.field === domain || ref.field.startsWith(`${domain}.`) || domain.startsWith(ref.field)) {
      out.push(i + 1);
    }
  });
  return out;
}

function formatAmendmentValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

function sedAmendmentToSection(
  amendment: SEDAmendment,
  refs: DataSource[],
): SectionAmendment {
  const citationsAffected = refsAffectedByDomain(amendment.variablePath, refs);
  return {
    amendmentId: amendment.id,
    fieldRange: { fieldPath: amendment.variablePath },
    originalText: formatAmendmentValue(amendment.oldValue),
    amendedText: formatAmendmentValue(amendment.newValue),
    amendedBy: amendment.createdByEmail || amendment.createdBy,
    amendedAt: amendment.appliedAt ?? amendment.createdAt,
    rationale: amendment.reason,
    citationsAffected,
  };
}

function editRecordToSection(edit: EditRecord, refs: DataSource[]): SectionAmendment {
  const fromText = extractRefNumbers(`${edit.originalValue}\n${edit.editedValue}`);
  const citationsAffected = fromText.length > 0
    ? fromText
    : refsAffectedByDomain(edit.field, refs);
  return {
    amendmentId: edit.id,
    fieldRange: { fieldPath: edit.field },
    originalText: edit.originalValue,
    amendedText: edit.editedValue,
    amendedBy: 'User correction',
    amendedAt: edit.editedAt,
    citationsAffected,
  };
}

/** Derive per-section amendments from SED store records + report edit history. */
export function deriveSectionAmendments(
  report: SiteRagReport,
  sedAmendments: SEDAmendment[] = [],
): Record<string, SectionAmendment[]> {
  const refs = report.references ?? [];
  const bySection: Record<string, SectionAmendment[]> = {};

  const push = (sectionKey: string, row: SectionAmendment) => {
    if (!bySection[sectionKey]) bySection[sectionKey] = [];
    bySection[sectionKey].push(row);
  };

  for (const amendment of sedAmendments) {
    if (!isActiveAmendmentStatus(amendment.status)) continue;
    const row = sedAmendmentToSection(amendment, refs);
    if (row.citationsAffected.length === 0) continue;
    push(row.fieldRange.fieldPath, row);
  }

  for (const edit of report.editHistory ?? []) {
    const row = editRecordToSection(edit, refs);
    if (row.citationsAffected.length === 0) continue;
    push(row.fieldRange.fieldPath, row);
  }

  return bySection;
}

/** Build footnote rows from a marked report's section amendments. */
export function buildAmendmentFootnotes(report: AmendmentStampedReport): AmendmentFootnote[] {
  const sections = (report.sectionAmendments ?? deriveSectionAmendments(report)) as Record<string, SectionAmendment[]>;
  const flat: SectionAmendment[] = Object.values(sections).flatMap((rows) => rows ?? []);
  const footnotes: AmendmentFootnote[] = [];
  const seen = new Set<string>();

  flat.forEach((row, i) => {
    if (seen.has(row.amendmentId)) return;
    seen.add(row.amendmentId);
    const markerId = MARKER_IDS[i] ?? String(i + 1);
    const label = row.fieldRange.fieldPath;
    const body = [
      `${label}: amended from "${truncate(row.originalText, 120)}" to "${truncate(row.amendedText, 120)}"`,
      row.rationale ? `Rationale: ${row.rationale}` : null,
    ].filter(Boolean).join('. ');
    footnotes.push({
      markerId,
      markerChar: markerIdToSuperscript(markerId),
      amendmentId: row.amendmentId,
      body,
      amendedBy: row.amendedBy,
      amendedAt: row.amendedAt,
      citationsAffected: row.citationsAffected,
    });
  });

  return footnotes;
}

function truncate(text: string, max: number): string {
  const cleaned = text.replace(/^"|"$/g, '').trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max)}…`;
}

/** Stamp citation markers + footnote metadata onto a report copy (no storage write). */
export function markCitationsWithAmendments(
  report: SiteRagReport,
  opts: { sedAmendments?: SEDAmendment[] } = {},
): AmendmentStampedReport {
  const sectionAmendments = deriveSectionAmendments(report, opts.sedAmendments ?? []);
  const withSections: AmendmentStampedReport = { ...report, sectionAmendments };
  const amendmentFootnotes = buildAmendmentFootnotes(withSections);

  if (amendmentFootnotes.length === 0) {
    return {
      ...report,
      sectionAmendments: Object.keys(sectionAmendments).length ? sectionAmendments : undefined,
      amendmentFootnotes: undefined,
      citationAmendmentMarkers: undefined,
      references: report.references,
    };
  }

  const citationAmendmentMarkers: Record<number, string[]> = {};
  const refs: DataSourceWithAmendment[] = [...(report.references ?? [])];

  for (const fn of amendmentFootnotes) {
    for (const refNum of fn.citationsAffected) {
      if (!citationAmendmentMarkers[refNum]) citationAmendmentMarkers[refNum] = [];
      if (!citationAmendmentMarkers[refNum]!.includes(fn.markerChar)) {
        citationAmendmentMarkers[refNum]!.push(fn.markerChar);
      }
      const idx = refNum - 1;
      const prev = refs[idx];
      if (!prev) continue;
      const markerIds = [...(prev.amendmentMarkerIds ?? [])];
      if (!markerIds.includes(fn.markerId)) markerIds.push(fn.markerId);
      refs[idx] = {
        ...prev,
        amendedSinceVersion: prev.amendedSinceVersion ?? report.sedVersionUsed,
        amendmentMarkerIds: markerIds,
      };
    }
  }

  return {
    ...report,
    references: refs,
    sectionAmendments,
    amendmentFootnotes,
    citationAmendmentMarkers,
  };
}

/** Reconstruct amendment chains for audit replay (SED amendments only). */
export function readAmendmentChain(
  siteId: string,
  reportId: string,
  amendments: SEDAmendment[],
): AmendmentChainLink[][] {
  const byPath = new Map<string, SEDAmendment[]>();
  for (const a of amendments) {
    const list = byPath.get(a.variablePath) ?? [];
    list.push(a);
    byPath.set(a.variablePath, list);
  }

  const chains: AmendmentChainLink[][] = [];

  for (const [variablePath, rows] of byPath) {
    const sorted = [...rows].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const chain: AmendmentChainLink[] = sorted.map((a) => ({
      amendmentId: a.id,
      siteId,
      reportId,
      variablePath,
      fieldPath: a.variablePath,
      status: a.status,
      amendedAt: a.appliedAt ?? a.createdAt,
      amendedBy: a.createdByEmail || a.createdBy,
      previousAmendmentId: a.reappliedFromAmendmentId ?? a.revertedFromAmendmentId,
    }));
    if (chain.length > 0) chains.push(chain);
  }

  return chains;
}
